import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  People as PeopleIcon,
  Work as WorkIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usersAPI, nominationsAPI, containersAPI } from '../services/api';
import Layout from './Layout';

interface DashboardStats {
  totalUsers: number;
  totalNominations: number;
  totalContainers: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalNominations: 0,
    totalContainers: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [usersRes, workflowsRes, containersRes] = await Promise.all([
          usersAPI.getAll(),
          nominationsAPI.getStats(),
          containersAPI.getWeightStats(),
        ]);

        setStats({
          totalUsers: usersRes.data.count || 0,
          totalNominations: workflowsRes.data.data.totalNominations || 0,
          totalContainers: containersRes.data.data.totalContainers || 0,
        });
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleUsersClick = () => {
    navigate('/users');
  };

  const handleWorkflowsClick = () => {
    navigate('/nominations');
  };

  const handleContainersClick = () => {
    navigate('/containers');
  };

  if (!user) {
    return null;
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, ml: '20px', mr: 'auto' }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3, color: '#333', fontWeight: 'bold' }}>
          Select a module to work with
        </Typography>
      {/* Stats Cards — one row, max 3: Total Nominations, Total Containers, Total Users */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px', mb: 4 }}>
        <Card
          onClick={handleWorkflowsClick}
          sx={{
            width: '100%',
            height: 311,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            backgroundColor: 'white',
            color: '#333',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: '#49738b', width: 80, height: 80, border: '2px solid #e0e0e0' }}>
                <WorkIcon sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', fontSize: '2.5rem', color: '#333' }}>
                {isLoading ? '...' : stats.totalNominations}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#666' }}>
                Total Nominations
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card
          onClick={handleContainersClick}
          sx={{
            width: '100%',
            height: 311,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            backgroundColor: 'white',
            color: '#333',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: '#56c785', width: 80, height: 80, border: '2px solid #e0e0e0' }}>
                <InventoryIcon sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', fontSize: '2.5rem', color: '#333' }}>
                {isLoading ? '...' : stats.totalContainers}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#666' }}>
                Total Containers
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card
          onClick={handleUsersClick}
          sx={{
            width: '100%',
            height: 311,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            backgroundColor: 'white',
            color: '#333',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: '#CA4300', width: 80, height: 80, border: '2px solid #e0e0e0' }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', fontSize: '2.5rem', color: '#333' }}>
                {isLoading ? '...' : stats.totalUsers}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#666' }}>
                Total Users
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>


      </Container>
    </Layout>
  );
};

export default Dashboard;
